/**
 * Record-detail and byte-window hooks (ADR-038 Phase G).
 *
 * The inspection half of the query surface: the full attribute set for
 * one record, and bounded windows of the bytes it came from.
 *
 * # Why bytes arrive one window at a time
 *
 * Disclosure is bounded at every edge (feature spec §1.4). The BFF serves
 * at most 64 KiB per request (D10) and audits each read, so the drawer
 * pages through the object rather than fetching it whole. Each window is
 * its own cache entry keyed by ``(type, id, offset)``, so paging back to
 * a window already read is instant and does not re-audit a read the
 * operator already made.
 *
 * # Integrity is fail-safe
 *
 * ``integrity`` is ``verified`` / ``unverified`` / ``unavailable``.
 * Anything other than ``verified`` must render as unconfirmed (D11 /
 * REQ-UI-124) — presenting an unverified reconstruction as authentic is
 * the wrong failure mode for a forensic surface, so the UI never treats
 * a missing field as good news.
 *
 * # Why the decoded message is a separate request
 *
 * ``useRecordMessage`` reads the same bytes rendered as their SAPIENT
 * field tree (REQ-QUERY-057). It is its own audited disclosure, so it is
 * gated on the operator asking for the formatted view rather than
 * fetched alongside the descriptor — exactly as the byte window is gated
 * on the drawer being open.
 *
 * Cross-references:
 *   - REQ-QUERY-040..042, 045, 049, 057; REQ-UI-123/124/129/185.
 *   - Feature spec §2.7; ADR-038 §Decision 4.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import type { components } from "@/lib/api-types.gen";

/** The full attribute set for one record plus its bytes descriptor. */
export type RecordDetail = components["schemas"]["RecordDetailResponse"];
/** What can be served for a record's originating bytes. */
export type BytesDescriptor = components["schemas"]["BytesDescriptor"];
/** A record's originating message rendered as its decoded field tree. */
export type DecodedMessage = components["schemas"]["DecodedMessageResponse"];

/** One bounded window of a record's originating bytes. */
export interface ByteWindow {
  readonly offset: number;
  readonly length: number;
  readonly total_size_bytes: number;
  /** ``verified`` / ``unverified`` / ``unavailable`` — never omitted. */
  readonly integrity: string;
  /** The window as lowercase hex (not base64 — the drawer renders a dump). */
  readonly data_hex: string;
}

/**
 * The per-request byte cap (D10). The BFF rejects a larger ``length``
 * with a 422, so this is a contract rather than a preference.
 */
export const MAX_BYTE_WINDOW = 65_536;

/** Bytes shown per drawer page. A screenful of hex, well under the cap. */
export const BYTE_WINDOW_SIZE = 1_024;

/** The queryable type whose records carry a downloadable object. */
export const RAW_BLOB_TYPE = "raw_blob";

/** Fetch every attribute the record's type declares (REQ-QUERY-040). */
export function useRecordDetail(
  typeId: string,
  recordId: string,
): UseQueryResult<RecordDetail, ApiError> {
  return useQuery<RecordDetail, ApiError>({
    queryKey: ["query", "record", typeId, recordId],
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/query/records/${encodeURIComponent(typeId)}/${encodeURIComponent(recordId)}`,
        { signal },
      );
      if (!isRecordDetail(raw)) {
        console.error("[meridian/query/use-record-detail] detail failed shape validation", raw);
        throw new ApiError(500, raw, "record detail did not match the expected shape");
      }
      return raw;
    },
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
  });
}

/**
 * Fetch one bounded window of a record's originating bytes.
 *
 * ``enabled`` is the drawer's open state: closed drawers must not read
 * bytes, because every read is an audited disclosure (REQ-QUERY-041 /
 * §7). ``length`` is clamped to the BFF's cap rather than sent and
 * rejected.
 */
export function useByteWindow(
  typeId: string,
  recordId: string,
  offset: number,
  length: number,
  options: { enabled: boolean },
): UseQueryResult<ByteWindow, ApiError> {
  const clamped = Math.min(Math.max(length, 1), MAX_BYTE_WINDOW);
  return useQuery<ByteWindow, ApiError>({
    queryKey: ["query", "record-bytes", typeId, recordId, offset, clamped],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        offset: String(offset),
        length: String(clamped),
      });
      const raw = await apiFetch<unknown>(
        `/api/query/records/${encodeURIComponent(typeId)}/${encodeURIComponent(recordId)}/bytes?${params.toString()}`,
        { signal },
      );
      if (!isByteWindow(raw)) {
        console.error("[meridian/query/use-record-detail] bytes failed shape validation", raw);
        throw new ApiError(500, raw, "byte window did not match the expected shape");
      }
      return raw;
    },
    enabled: options.enabled,
    // A record's bytes are immutable — the window never needs refetching
    // once read, and re-reading would emit a second audit event for a
    // disclosure the operator already made.
    staleTime: Number.POSITIVE_INFINITY,
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
  });
}

/**
 * Fetch a record's originating message as its decoded field tree
 * (REQ-QUERY-057 / REQ-UI-185).
 *
 * The readable rendering of the same bytes the window serves: for a
 * SAPIENT record the wire form is protobuf, so the hex dump is not
 * something an analyst reads and a text decode of it is fragments of
 * string fields inside binary framing. This is the form that answers
 * *what did the producer actually report*.
 *
 * ``enabled`` is the formatted view being asked for. Rendering the
 * content discloses it just as a window read does, so a closed drawer —
 * or an open one showing hex — must not emit the audit event
 * (REQ-QUERY-041 §7).
 *
 * A ``404`` is a normal, expected answer for a type that carries no
 * message (a track is a derivation; a raw blob is not a SAPIENT
 * message), and a ``413`` for a message past the rendering ceiling. Both
 * are states the caller must *explain*, so neither is retried.
 */
export function useRecordMessage(
  typeId: string,
  recordId: string,
  options: { enabled: boolean },
): UseQueryResult<DecodedMessage, ApiError> {
  return useQuery<DecodedMessage, ApiError>({
    queryKey: ["query", "record-message", typeId, recordId],
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/query/records/${encodeURIComponent(typeId)}/${encodeURIComponent(recordId)}/message`,
        { signal },
      );
      if (!isDecodedMessage(raw)) {
        console.error("[meridian/query/use-record-detail] message failed shape validation", raw);
        throw new ApiError(500, raw, "decoded message did not match the expected shape");
      }
      return raw;
    },
    enabled: options.enabled,
    // A record's message is immutable; re-reading would emit a second audit
    // event for a disclosure the operator already made.
    staleTime: Number.POSITIVE_INFINITY,
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
  });
}

/**
 * The same-origin whole-blob download URL (REQ-QUERY-044).
 *
 * A streamed file download, so it cannot go through :func:`apiFetch`
 * (which parses JSON) — the UI navigates to this URL and the session
 * cookie rides along on the same-origin GET. The BFF emits
 * ``query.blob.downloaded`` server-side. Callers must check the detail
 * response's ``downloadable`` verdict first and disable the affordance
 * with its reason rather than offering a request that will fail
 * (REQ-UI-129 / REQ-QUERY-049).
 */
export function blobDownloadUrl(recordId: string): string {
  return `/api/query/records/${RAW_BLOB_TYPE}/${encodeURIComponent(recordId)}/download`;
}

/** Shape guard at the trust boundary (coding-standards §2.2). */
function isRecordDetail(value: unknown): value is RecordDetail {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.type === "string" &&
    typeof obj.id === "string" &&
    typeof obj.attributes === "object" &&
    obj.attributes !== null &&
    typeof obj.bytes === "object" &&
    obj.bytes !== null
  );
}

/** Shape guard at the trust boundary (coding-standards §2.2). */
function isDecodedMessage(value: unknown): value is DecodedMessage {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.type === "string" &&
    typeof obj.id === "string" &&
    typeof obj.format === "string" &&
    typeof obj.integrity === "string" &&
    typeof obj.message === "object" &&
    obj.message !== null
  );
}

/** Shape guard at the trust boundary (coding-standards §2.2). */
function isByteWindow(value: unknown): value is ByteWindow {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.offset === "number" &&
    typeof obj.length === "number" &&
    typeof obj.total_size_bytes === "number" &&
    typeof obj.integrity === "string" &&
    typeof obj.data_hex === "string"
  );
}
